import {useAuthStore} from '@store';
import VueRouter from "vue-router";
import {AGENT, BROKER, SELLER, TRANSACTION_COORDINATOR} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/LuAccountTypeAppResource";
import {refreshUserData} from "@helpers/user";
import {
    getActiveThirdPartyPlatformList,
    setupAxiosInterceptors,
} from "@helpers/api-helper";
import {debugf, decodeBase64, encodeBase64, replaceAll} from "@helpers/string";
import {trim} from "lodash";

const url_list = {
    google_ads: (ads_id) => 'https://google.com/' + ads_id,
    yahoo_ads: 'https://yahoo.com',
    protect_v2: window.APP_ENV === 'production' ? 'https://protect.sellersshield.com/' : 'https://staging.sellersshield.com/',
};

export const isRequestProxyByV2 = (routeQuery = {}) => {
    let proxyByV2 = false;
    const storeAuth = useAuthStore();

    if (!Boolean(storeAuth.proxyByV2)) {
        //test referrer for older version v2 domain
        let referrer = document.referrer || window.referrer,

            testUrlV2 = replaceAll(
                replaceAll(url_list.protect_v2, 's:', 's?:'),
                '.', '\.'
            );

        proxyByV2 = Boolean(routeQuery?.forceProxyByV2) ||
            !!referrer && !!referrer.match(new RegExp('^' + testUrlV2 + '(\\/|$)', 'i'));

        //return all responses via v2
        !proxyByV2 || storeAuth.toggleOnProxyByV2();
    }

    return storeAuth.proxyByV2;
}

export const proxyRedirectUrlByV2 = (url) => {
    if (!!url) {
        const storeAuth = useAuthStore();

        return storeAuth.proxyByV2
            ? url_list.protect_v2 + 'api/v1/partner/redirect?redirectUrl=' + encodeURI(withInjectedMeUuid(url))
            : withInjectedMeUuid(url);
    }
    return null;
};

export const isOnDomain = (locHref, compareHref = null, hasSameDomainAsOrigin = false) => {
    return getDomainName(locHref) === getDomainName(compareHref || window.env.APP_URL) 
            && (!hasSameDomainAsOrigin || isOnDomain(locHref, window.location.href));
}

export const isInternalRedirect = (locationHref, hasSameDomainAsOrigin = true) => {
    return locationHref[0] === '/' ||
        isOnDomain(locationHref, null, hasSameDomainAsOrigin) ||
        isOnDomain(locationHref, appUrlOrPathnameByUserType('agent', true), hasSameDomainAsOrigin) ||
        isOnDomain(locationHref, appUrlOrPathnameByUserType('seller', true), hasSameDomainAsOrigin) ||
        isOnDomain(locationHref, appUrlOrPathnameByUserType('broker', true), hasSameDomainAsOrigin) ||
        isOnDomain(locationHref, appUrlOrPathnameByUserType('transactioncoordinator', true), hasSameDomainAsOrigin)
        ;
}

export const isOnMainDomain = () => {
    // verifies hostnames match between location.origin and env.APP_URL 
    // ... i.e. if window.locatin.origin is "https://protect3.sellersshield.com/auth"
    return isOnDomain(window.location.origin);
}

export const isSubdomainSetup = () => {
    return isOnMainDomain() && (
        Boolean(window.env?.APP_AGENT_URL) ||
        Boolean(window.env?.APP_SELLER_URL) ||
        Boolean(window.env?.APP_BROKER_URL) ||
        Boolean(window.env?.APP_TC_URL)
    );
}

export const getDomainName = (url) => {
    // for an input like "http://mycurrentip:92/auth", this will return "mycurrentip"
    // for an input like "http://protect3.sellersshield.com/auth", this will return "protect3.sellersshield.com"
    try {
        if (!url || url === Boolean(url)) {
            return null;
        }
        if (url[0] !== '/') {
            return new URL(url).hostname;
        }
    } catch (e) {
        console.log('router-helper.js - getDomainName() error!' , {url, e});
    }
    return url;
}

export const appUrlOrPathnameByUserType = (userType, fullPath = false) => {
    userType = `${userType}`.toLowerCase();
    fullPath = (fullPath ? window.env?.APP_URL : '');

    switch (userType) {
        case `${SELLER}`:
        case 'seller':
            return window.env?.APP_SELLER_URL || `${fullPath}/seller`;
        case `${AGENT}`:
        case 'agent':
            return window.env?.APP_AGENT_URL || `${fullPath}/agent`;
        case `${BROKER}`:
        case 'broker':
            return window.env?.APP_BROKER_URL || `${fullPath}/broker`;
        case `${TRANSACTION_COORDINATOR}`:
        case 'transactioncoordinator':
        case 'transactionCoordinator':
                return window.env?.APP_TC_URL || `${fullPath}/transactioncoordinator`;            
    }
    return window.env?.APP_URL;
};

export const withInjectedMeUuid = (url, acceptedHost = []) => {
    const storeAuth = useAuthStore();

    let validRoute = url && !(/\/(?:auth|register|forgot-password|logout)/.test(url)) && isSubdomainSetup(),
        meParam = validRoute ? `/${(storeAuth.me || {}).uuid}`.replace(/\//, '') : null;

    //inject user uuid in url if we need to
    if (validRoute && meParam && url[0] !== '/' && url.indexOf(`/${meParam}`) === -1) {
        try {
            let currentDomain = getDomainName(url),
                validHosts = (
                    (acceptedHost && acceptedHost.length > 0 ? acceptedHost : null) ||
                    [window.env?.APP_AGENT_URL, window.env?.APP_SELLER_URL, window.env?.APP_BROKER_URL]
                ).map(h => h ? getDomainName(h) : '').filter(h => !!h);

            if (currentDomain && (validHosts.length === 0 || validHosts.includes(currentDomain))) {
                meParam = encodeBase64(meParam);
                if (url.indexOf(`/${meParam}`) === -1) {
                    let $parts = new URL(url);
                    url = $parts.origin.replace(/\/$/, '') + `/${meParam}${$parts.pathname}${$parts.search}`;
                }
            }
        } catch (e) {
            console.log(e);
        }
    }

    return url;
};

export const pageRedirect = async (origin, endpointAndParam = '', pushStateRedirect = true) => {
    // weird csrf token mismatch bug fix
    // don't remove the origin cache variable or the redirect by vanilla js
    if (!origin || origin[0] === '/') {
        origin = window.location.origin + `${origin}${endpointAndParam}`;
    } else {
        origin = withInjectedMeUuid(`${origin}${endpointAndParam}`);
        pushStateRedirect = pushStateRedirect && isInternalRedirect(origin);
    }


    // console.log('checkIf--pageRedirectApp ---', origin);
    await forceUnloadApp('', !pushStateRedirect, false, {mountable: false, disabled: true});

    try {
        //pushStateRedirect = false;
        // MIGHT BE UNSTABLE, we'll see in time if we keep this !!!!!
        if (pushStateRedirect) {
            //works only if we have same origin for redirects
            // debugf(["1.window redirect push/assign state:", origin]);

            if ("undefined" !== typeof history.pushState) {
                await window.history.pushState(
                    {}, 
                    null,
                    origin
                );
            } else {
                await window.location.assign(origin);
            }

            let isPartnerRoute = matchCommonIntegrationsRoute();
            setTimeout(async () => await unloadAppAndSiblings(
                'integrations',
                {
                    mountable: !!isPartnerRoute,
                    disabled: !isPartnerRoute,
                    mountableOther: true,
                    disabledOther: false
                }
            ), 5);
        }
    } catch (e) {
        pushStateRedirect = false;
        console.error('router-helper.js - pageRedirect() error!', e);
    }

    if (!pushStateRedirect) {
        toggleSSPALoader('on');

        // debugf(["2.window redirect:", origin]);

        window.location.href = origin;
    }

    let checkLoad;
    await (checkLoad = async () => {
        window.onLoad && await setTimeout(() => checkLoad(), 50);
    })();
};

export const pageRedirectMainLogout = async () => {
    const storeAuth = useAuthStore();

    await storeAuth.logout();

    await pageRedirect(window.env.APP_URL, isOnMainDomain() ? '/auth' : '/logout', false);
}

export const updateLocationTo = async (urlOrPathname, useHost = false, onlyGuestAuthRegister = false, pushStateRedirect = true) => {
    let isOnGuest = isOnMainDomain(),
        pathString = window.location.pathname + window.location.search,
        isOnAuthRegister = /\/(?:auth|register|forgot-password|register-vwo)/.test(pathString),
        forceRedirect = Boolean(urlOrPathname) && window.location.href.indexOf(urlOrPathname) === -1 && window.env?.APP_URL == window.location.href,
        Url = (useHost ? '' : window.env?.APP_URL) + urlOrPathname + (
            onlyGuestAuthRegister
                ? pathString.replace(
                    useHost ? ' ' : urlOrPathname,
                    ''
                )
                : ''
        );

    if ([false, 'force'].includes(onlyGuestAuthRegister) || isOnGuest && isOnAuthRegister || forceRedirect) {
        const storeAuth = useAuthStore();

        if (!!storeAuth.loggedIn) {
            //jump over some redirects through app's routers to save some time;
            let urlObj = new URL(Url);

            Url = urlObj.origin +
                ((urlObj.pathname !== '/' ? urlObj.pathname : '') || '/auth').replace(/\/(?:auth|register|register-vwo)/, '/dashboard') +
                urlObj.search;
        }
        return await pageRedirect(Url, "", pushStateRedirect);
    }

    return false;
};

export const appPageRedirect = async (appName, endpointAndParam = '', pushStateRedirect = true) => {
    let moduleHostOrType = appName ? appUrlOrPathnameByUserType(appName) : null;

    if (Boolean(moduleHostOrType) && !isOnDomain(moduleHostOrType, window.location.origin)) {
        return await updateLocationTo(
            moduleHostOrType + endpointAndParam,
            moduleHostOrType !== `/${appName}`,
            false,
            pushStateRedirect
        );
    }

    //should be on the same domain if we reached here
    return await pageRedirect(appName === moduleHostOrType ? appName : null, endpointAndParam, pushStateRedirect);
};

export const beforeEnterRoute = async (appBaseUrl, to, from, next, allowedRoles = []) => {
    const storeAuth = useAuthStore();

    const me = storeAuth.me || {};

    if (Boolean(me?.uuid || me?._uuid)) {
        let gogo = allowedRoles.length === 0 || allowedRoles.some(allowedRole => allowedRole(me));
        !gogo || !storeAuth.accessToken || setupAxiosInterceptors(storeAuth.accessToken);

        gogo
            ? next()
            : await pageRedirect(appBaseUrl, '/dashboard');
    } else {
        await pageRedirect(appBaseUrl);
    }
};

export const beforeEachRoute = async (to, from, next, publicPageList = null, canRefreshData = true, boolReturn = false) => {
    const storeAuth = useAuthStore();

    let toNamePath = trim(to.name || to.path, " \r\n\t\\/");

    if (toNamePath.indexOf('logout') > -1) {
        debugf('beforeEachRoute/ #logout-route');
        return boolReturn ? true : next();
    }

    debugf('beforeEachRoute/ #0', {
        toNamePath: toNamePath,
        fromNamePath: from.name || from.path,
        canRefreshData,
        boolReturn
    }, to?.query);

    let APP_URL = window.env?.APP_URL,
        me = storeAuth.me,
        uuid = me?.uuid || me?._uuid,
        loggedIn = !!storeAuth.loggedIn && !!uuid,
        isOnGuest = isOnMainDomain(),
        isOnCommonPage = (publicPageList || ['root', 'auth', 'register', 'forgot-password', 'register-vwo']).includes(toNamePath),
        isPublicPage = isOnCommonPage || Boolean(to?.meta?.isPublic) || ['not-found', 'guest-help'].includes(toNamePath);
    if (to.path === from?.path) {
        debugf('beforeEachRoute/ #1/ toPath matches fromPath', {loggedIn, isOnGuest, isPublicPage});

        if (!boolReturn && !Boolean(toNamePath)) {
            debugf('beforeEachRoute/ #1.1');

            return isOnGuest && !loggedIn
                ? await pageRedirect(APP_URL, '/auth')
                : await pageRedirect(
                    loggedIn ? null : APP_URL,
                    loggedIn ? '/dashboard' : '/logout',
                    false
                );
        }

        return false;

    } else {
        debugf('beforeEachRoute/ #2/ toPath differs fromPath', {loggedIn, isOnGuest, isPublicPage});

        if (canRefreshData) {
            if (loggedIn && !isPublicPage) {
                console.log('router-helper.js beforeEachRoute() calling refreshUserData', {a: uuid, b: false, c: true});
                loggedIn = await refreshUserData(uuid, false, true);
                debugf('beforeEachRoute/ #2.1', {loggedIn, uuid});
            }

            if (['invite-seller', 'order-details'].includes(toNamePath)) {
                debugf('beforeEachRoute/ 2.2');
                await getActiveThirdPartyPlatformList(true);
            }
        }

        if (isPublicPage) {
            let invitationToken = to.query.token || to.query.tokenSms,
                requireUnAuth = to.matched.some(record => record?.meta?.requireUnAuth);

            if (loggedIn && (isOnCommonPage || requireUnAuth) && !invitationToken) {
                debugf('beforeEachRoute/ #dashboard', {isOnCommonPage, requireUnAuth});
                return boolReturn ? true : await pageRedirect(window.location.origin, '/dashboard');
            }
        } else if (!loggedIn) {

            if (boolReturn) {
                return false;
            }

            return await pageRedirect(APP_URL, isOnGuest ? '/auth' : '/logout', false);
        }

        debugf('beforeEachRoute/ #next');
        return boolReturn ? true : next();
    }
};

export const afterEachRoute = (to, from, failure) => {
    if (VueRouter.isNavigationFailure(failure)) {
        if (VueRouter.isNavigationFailure(failure, 4)) {
            //ask and/or do something on abort
        } else if (window.env?.APP_ENV !== 'production') {
            console.warn('failed navigation', failure);
        }
    }
};

export const paramsFromQueryString = ($routeQuery = null) => {
    let urlParams = new URLSearchParams(window.location.search),
        rquery = $routeQuery || {},
        urlParamGet = (name, def = '') => urlParams.get(name) || rquery[name] || def,
        tokenSms = urlParamGet('tokenSms'),
        params = {
            tokenSms: tokenSms,
            token: urlParamGet('token', tokenSms),
            redirectUrl: urlParamGet('redirectUrl', ''),

            email: urlParamGet('email') || urlParamGet('emailAddress') || '',
            state: urlParamGet('state') || urlParamGet('licenseState') || urlParamGet('propertyState'),
            type: urlParamGet('type') || urlParamGet('accountType'),

            firstName: urlParamGet('firstName'),
            lastName: urlParamGet('lastName'),

            companyName: urlParamGet('companyName') || urlParamGet('company'),

            licenseId: urlParamGet('licenseId'),
            nrdsId: urlParamGet('nrdsId'),

            zipCode: urlParamGet('zipCode') || urlParamGet('zip') || urlParamGet('propertyZip') || '',
            address: urlParamGet('street') || urlParamGet('address') || urlParamGet('propertyStreetAddress') || '',
            city: urlParamGet('city') || urlParamGet('propertyCity'),

            referringPlatform: urlParamGet('referringPlatform'),
            accessToken: urlParamGet('accessToken'),
            authCallbackUrl: urlParamGet('authCallbackUrl'),
            returnUrl: urlParamGet('returnUrl'),

            developerListingId: urlParamGet('developerListingId'),

            phone: urlParamGet('phone'),

            primarySellerFirstName: urlParamGet('primarySellerFirstName'),
            primarySellerLastName: urlParamGet('primarySellerLastName'),
            primarySellerEmailAddress: urlParamGet('primarySellerEmailAddress'),
            primarySellerPhone: urlParamGet('primarySellerPhone'),

            secondarySellerFirstName: urlParamGet('secondarySellerFirstName'),
            secondarySellerLastName: urlParamGet('secondarySellerLastName'),
            secondarySellerEmailAddress: urlParamGet('secondarySellerEmailAddress'),
            secondarySellerPhone: urlParamGet('secondarySellerPhone'),

            propertyAddress: urlParamGet('propertyAddress'),
            propertyCity: urlParamGet('propertyCity'),
            propertyState: urlParamGet('propertyState'),
            propertyZip: urlParamGet('propertyZip'),
        };

    Object.keys(params).forEach(key => {
        if (!!params[key]) {
            let testString, decodeTries = 10;
            do {
                // remove all possible wrapping empty spaces or eof
                params[key] = params[key].trim();

                // if we have urls we are only interested in testing if first part containing domain is decoded
                testString = params[key].split('?')[0];

                // make sure to avoid the above erasing everything on strings starting with ?
                if (!/\%[0-9]/i.test(testString || params[key])) {
                    break;
                }

                params[key] = decodeURIComponent(params[key]);
            } while (--decodeTries > 0);

            if (key === 'email') {
                params.email = params.email.replace(' ', '+');
            }
        }
    });

    return params;
}

export const paramsToQueryString = (params) => {
    const query = new URLSearchParams(''),
        regex = new RegExp('[\s?=&#:]');

    Object.keys(params).forEach(key => {
        if (key && params[key] !== null && (Boolean(params[key]) || params[key] === 0)) {
            query.set(key, regex.test(params[key]) ? encodeURIComponent(params[key]) : params[key])
        }
    });

    return query.toString();
}

export const matchFirstUuidInUrl = (location) => {
    let matches, posibleUuid = location.pathname.split('/')[1] || '';

    if (posibleUuid !== '') {
        try {
            matches = posibleUuid.match(/^([\w]{8}(-[\w]{4}){3}-[\w]{12})/) || [];
            if (matches.length === 0) {
                matches = decodeBase64(posibleUuid).match(/^([\w]{8}(-[\w]{4}){3}-[\w]{12})/) || [];
            }
            return {
                matchUuid: matches[1] || null,
                urlToken: posibleUuid,
            };
        } catch (e) {
        }
    }

    return {
        matchUuid: null,
        urlToken: null,
    }
}


export const isModuleOfType = (location, moduleHostOrType = '', checkHost = false) => {
    let partString = checkHost ? location.host : location.pathname,
        validatorString = checkHost ? (getDomainName(moduleHostOrType) || moduleHostOrType) : moduleHostOrType;

    return partString.startsWith(validatorString);
}

const filterBagApps = (APP = '', except = false) => {
    let list = [], name, i,
        bagApps = Object.keys(window.SSPA?.bag || {}),
        apps = Boolean(APP && (Array.isArray(APP) ? APP.length > 0 : true))
            ? Array.isArray(APP) ? [...APP] : [APP]
            : bagApps;

    for (i in bagApps) {
        if (name = bagApps[i]) {
            if (except) {
                apps.indexOf(name) > -1 || list.push(name);
            } else {
                apps.indexOf(name) === -1 || list.push(name);
            }
        }
    }

    return list;
}

export const forceUnloadApp = async (APP = '', triggerChange = true, except = false, {
    mountable = true,
    disabled = false,
} = {}) => {

    let name, i, apps = filterBagApps(APP, except),
        affectAppMount = typeof mountable === 'boolean' && typeof disabled === 'boolean';

    if (apps.length > 0 && affectAppMount) {
        await toggleEnableApp(apps, false, false, {mountable, disabled});
    }

    // debugf('checkIf--forceUnloadApp', {
    //     apps,
    //     triggerChange,
    //     affectAppMount: Boolean(affectAppMount)
    // });

    for (i in apps) {
        name = apps[i];
        window.SSPA && await window.SSPA.unloadApplication(`${name}/app`);
    }

    //don't await this trigger (blocks everything sometimes);
    triggerChange && window.SSPA && window.SSPA.triggerAppChange();
};


export const toggleEnableApp = async (APP = '', triggerChange = true, except = false, {
    mountable = true,
    disabled = false,
} = {}) => {

    let thisAPP, name, i, apps = filterBagApps(APP, except);

    // debugf('checkIf--toggleEnableApp', {apps, mountNextTime: mountable, disabled, triggerChange});

    for (i in apps) {
        thisAPP = window.SSPA?.bag[name = apps[i]];

        if (thisAPP) {
            thisAPP.mountable = mountable;
            thisAPP.disabled = disabled;
        }
    }

    //don't await this trigger (blocks everything sometimes);
    triggerChange && window.SSPA && window.SSPA.triggerAppChange();
};

export const unloadAppAndSiblings = async (APP, {
    mountable = true,
    disabled = false,
    mountableOther = '',
    disabledOther = '',
} = {}) => {
    // debugf(`checkIf--forceUnloadApp / unloadAppAndSiblings:`);

    // when unloading all apss we need to grant special attention to apps that are only sometimes requiered to be mountable like integrations
    await forceUnloadApp('integrations', false, true, {mountable: mountableOther, disabled: disabledOther});
    await forceUnloadApp('integrations', true, false, {mountable, disabled});

    //don't await this trigger (blocks everything sometimes);
    window.SSPA && setTimeout(() => window.SSPA.triggerAppChange(), 1);
}

export const toggleSSPALoader = ($force = null) => {
    let loader = 'loader-single-spa',
        loaderEl = document.getElementById(loader);

    if (!$force || !loaderEl && $force === 'on' || loaderEl && $force === 'off') {
        if (loaderEl) {
            setTimeout(() => loaderEl.remove(), 10);
        } else {
            document.body.innerHTML += `<div id="${loader}"/>`;
        }
    }
};

export const matchCommonGuestRoute = (path = '') => {
    return Boolean((path || window.location.pathname).match(/\/(?:logout|download-pdfs|escrow\/officer\/invoice|disclosure\/[^\/]*\/order\/[^\/]*\/sign)/));
}

export const matchCommonIntegrationsRoute = (location = null) => {
    let pathname = (location || window.location).pathname,
        search = (location || window.location).search;
    return (Boolean((pathname).match(/\/partner\/[a-z]/i)) && search.indexOf('referringPlatform=') > -1);
}

export const scrollToTop = (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
}

export default url_list;